import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { SKIP_REASONS } from '../lib/constants';
import { Plus, Trash2, X, CalendarOff, MapPinOff, ChevronDown, Edit2 } from 'lucide-react';

export default function SkipsOOTPage() {
  const [subs, setSubs] = useState([]);
  const [skips, setSkips] = useState([]);
  const [oots, setOots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showOOTModal, setShowOOTModal] = useState(false);
  const [editSkip, setEditSkip] = useState(null);
  const [editOOT, setEditOOT] = useState(null);
  const [activeTab, setActiveTab] = useState('skips');

  const [skipForm, setSkipForm] = useState({ subscriberId: '', date: '', reason: 'Customer request', notes: '' });
  const [ootForm, setOotForm] = useState({ subscriberId: '', fromDate: '', toDate: '', reason: '' });

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, skipsRes, ootsRes] = await Promise.all([api.getSubscribers({}), api.getSkips({}), api.getOOT({})]);
      setSubs(subsRes.data); setSkips(skipsRes.data); setOots(ootsRes.data);
    } catch (err) { console.error('Fetch error:', err.message); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const getSubName = (id) => { const s = subs.find(s => s.id === id); return s ? `${s.name} (${s.apt} / ${s.flat})` : id; };

  const openNewSkip = () => { setEditSkip(null); setSkipForm({ subscriberId: '', date: '', reason: 'Customer request', notes: '' }); setShowSkipModal(true); };
  const openEditSkip = (skip) => { setEditSkip(skip); setSkipForm({ subscriberId: skip.subscriberId, date: skip.date, reason: skip.reason || 'Customer request', notes: skip.notes || '' }); setShowSkipModal(true); };

  const openNewOOT = () => { setEditOOT(null); setOotForm({ subscriberId: '', fromDate: '', toDate: '', reason: '' }); setShowOOTModal(true); };
  const openEditOOT = (oot) => { setEditOOT(oot); setOotForm({ subscriberId: oot.subscriberId, fromDate: oot.fromDate, toDate: oot.toDate, reason: oot.reason || '' }); setShowOOTModal(true); };

  const handleSkipSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editSkip) { await api.updateSkip(editSkip.id, skipForm); }
      else { await api.createSkip(skipForm); }
      setShowSkipModal(false); fetchData();
    } catch (err) { console.error('Save error:', err.message); }
  };

  const handleOOTSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editOOT) { await api.updateOOT(editOOT.id, { fromDate: ootForm.fromDate, toDate: ootForm.toDate, reason: ootForm.reason }); }
      else { await api.createOOT(ootForm); }
      setShowOOTModal(false); fetchData();
    } catch (err) { console.error('Save error:', err.message); }
  };

  const handleDeleteSkip = async (id) => { if (!window.confirm('Delete this skip?')) return; await api.deleteSkip(id); fetchData(); };
  const handleDeleteOOT = async (id) => { if (!window.confirm('Delete this OOT block?')) return; await api.deleteOOT(id); fetchData(); };

  const activeSubs = subs.filter(s => s.status === 'active' || s.status === 'out-of-town');

  return (
    <div data-testid="skips-oot-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Skips & Out-of-Town</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>Manage delivery skips and out-of-town blocks</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="add-skip-button" onClick={openNewSkip} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>
            <CalendarOff className="w-4 h-4" /> Record Skip
          </button>
          <button data-testid="add-oot-button" onClick={openNewOOT} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)' }}>
            <MapPinOff className="w-4 h-4" /> Add Out-of-Town
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg" style={{ background: '#161311' }}>
        {['skips', 'oot'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all"
            style={{ background: activeTab === tab ? 'rgba(200,169,110,0.1)' : 'transparent', color: activeTab === tab ? '#C8A96E' : '#78716C' }}>
            {tab === 'skips' ? `Skip Log (${skips.length})` : `Out-of-Town (${oots.length})`}
          </button>
        ))}
      </div>

      <div className="rounded-xl p-4 text-sm" style={{ background: 'rgba(200,169,110,0.05)', border: '1px solid rgba(200,169,110,0.15)', color: '#A8A29E' }}>
        <strong style={{ color: '#C8A96E' }}>Carry-forward rule:</strong> Skipped weekdays are <strong>not deducted</strong> from the delivery quota. OOT blocks auto carry-forward all weekdays. You can <strong>edit dates</strong> if a customer returns early.
      </div>

      {loading ? <div className="text-center py-12" style={{ color: '#78716C' }}>Loading...</div> : activeTab === 'skips' ? (
        <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="overflow-x-auto">
            <table className="w-full gm-table">
              <thead><tr><th>Subscriber</th><th>Date</th><th>Reason</th><th>Notes</th><th>Carry Forward</th><th></th></tr></thead>
              <tbody>
                {skips.length === 0 ? <tr><td colSpan={6} className="text-center py-8" style={{ color: '#78716C' }}>No skip records</td></tr> :
                skips.map(skip => (
                  <tr key={skip.id}>
                    <td style={{ color: '#F5F5F4' }}>{getSubName(skip.subscriberId)}</td>
                    <td>{skip.date}</td>
                    <td>{skip.reason}</td>
                    <td className="text-xs">{skip.notes}</td>
                    <td><span className="badge-active px-2 py-0.5 rounded-full text-xs font-semibold">Yes</span></td>
                    <td>
                      <div className="flex gap-1">
                        <button data-testid={`edit-skip-${skip.id}`} onClick={() => openEditSkip(skip)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Edit2 className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-skip-${skip.id}`} onClick={() => handleDeleteSkip(skip.id)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
          <div className="overflow-x-auto">
            <table className="w-full gm-table">
              <thead><tr><th>Subscriber</th><th>From</th><th>To</th><th>Weekdays</th><th>Reason</th><th></th></tr></thead>
              <tbody>
                {oots.length === 0 ? <tr><td colSpan={6} className="text-center py-8" style={{ color: '#78716C' }}>No out-of-town blocks</td></tr> :
                oots.map(oot => (
                  <tr key={oot.id}>
                    <td style={{ color: '#F5F5F4' }}>{getSubName(oot.subscriberId)}</td>
                    <td>{oot.fromDate}</td>
                    <td>{oot.toDate}</td>
                    <td><span className="badge-oot px-2 py-0.5 rounded-full text-xs font-semibold">{oot.weekdays} days</span></td>
                    <td>{oot.reason}</td>
                    <td>
                      <div className="flex gap-1">
                        <button data-testid={`edit-oot-${oot.id}`} onClick={() => openEditOOT(oot)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Edit2 className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-oot-${oot.id}`} onClick={() => handleDeleteOOT(oot.id)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Skip Modal */}
      {showSkipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editSkip ? 'Edit Skip' : 'Record Skip'}</h3>
              <button onClick={() => setShowSkipModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSkipSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Subscriber *</label>
                <div className="relative">
                  <select data-testid="skip-subscriber" required value={skipForm.subscriberId} onChange={e => setSkipForm({...skipForm, subscriberId: e.target.value})} disabled={!!editSkip} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    <option value="">-- select --</option>
                    {activeSubs.map(s => <option key={s.id} value={s.id}>{s.name} ({s.apt} / {s.flat})</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Skip Date *</label>
                <input data-testid="skip-date" type="date" required value={skipForm.date} onChange={e => setSkipForm({...skipForm, date: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Reason</label>
                <div className="relative">
                  <select value={skipForm.reason} onChange={e => setSkipForm({...skipForm, reason: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    {SKIP_REASONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Notes</label>
                <input value={skipForm.notes} onChange={e => setSkipForm({...skipForm, notes: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="save-skip-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>{editSkip ? 'Update Skip' : 'Save Skip'}</button>
                <button type="button" onClick={() => setShowSkipModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OOT Modal */}
      {showOOTModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editOOT ? 'Edit Out-of-Town' : 'Add Out-of-Town Block'}</h3>
              <button onClick={() => setShowOOTModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleOOTSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Subscriber *</label>
                <div className="relative">
                  <select required value={ootForm.subscriberId} onChange={e => setOotForm({...ootForm, subscriberId: e.target.value})} disabled={!!editOOT} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
                    <option value="">-- select --</option>
                    {activeSubs.map(s => <option key={s.id} value={s.id}>{s.name} ({s.apt} / {s.flat})</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
                </div>
              </div>
              {editOOT && (
                <div className="rounded-lg p-3 text-sm" style={{ background: 'rgba(200,169,110,0.05)', border: '1px solid rgba(200,169,110,0.15)', color: '#A8A29E' }}>
                  Editing dates will automatically recalculate weekdays and adjust the subscriber's remaining deliveries.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>From Date *</label>
                  <input type="date" required value={ootForm.fromDate} onChange={e => setOotForm({...ootForm, fromDate: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>To Date *</label>
                  <input type="date" required value={ootForm.toDate} onChange={e => setOotForm({...ootForm, toDate: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Reason</label>
                <input value={ootForm.reason} onChange={e => setOotForm({...ootForm, reason: e.target.value})} placeholder="e.g. Dasara holiday, hometown visit" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="save-oot-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold" style={{ background: '#C8A96E', color: '#000' }}>{editOOT ? 'Update OOT' : 'Save Block'}</button>
                <button type="button" onClick={() => setShowOOTModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
